/**
 * Installation RPC definitions
 *
 * Customer-facing authenticated endpoints for the caller's own agent fleet.
 * The lifecycle endpoint (`requestCancellation`) dispatches the VPS
 * deprovisioning workflow; the read endpoints (`list`/`get`/`metrics`/
 * `summary`) back the customer dashboard — all scoped to the authenticated
 * caller's customer (resolved from `CurrentActor`, never client input) and
 * read-only.
 *
 * The read endpoints surface only derived host strings
 * (`gatewayHost`/`webHost`), never `frpToken`, `agentSecret` or `vncPassword`.
 * The single deliberate exception is `gatewayToken`: it decrypts and returns the
 * agent's OpenClaw gateway access token to the *owning* customer so they can
 * complete the login the gateway URL lands on — ownership-guarded like every
 * other per-agent read, and never logged.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { CloudProvider, DeploymentType, SeatBillingMode } from "@fm/db/models/AgentInstallation";
import { Schema } from "effect";
import { AttentionItem } from "../admin/dashboard.rpc.ts";
import { InstallActivityEvent } from "../admin/installs.rpc.ts";
import { NodeHealth, NodeMetrics } from "../admin/nodes.rpc.ts";
import { AuthMiddleware } from "../auth.tags.ts";
import { BrandMiddleware } from "../brand.tags.ts";
import {
  DatabaseError,
  Forbidden,
  RecordNotFoundError,
  SubscriptionError,
  Unauthorized,
  ValidationError,
  ValkeyError,
} from "../errors.ts";
import { PendingPairingRequest } from "../mgmt.ts";
import { SupportContact } from "./onboarding.rpc.ts";

/**
 * Input for the customer cancellation request endpoint.
 * @since 1.0.0
 */
export const InstallationCancelInput = Schema.Struct({
  installationId: Schema.String,
});

/**
 * Response acknowledging a scheduled-cancellation REQUEST (CLA-1003). Requesting
 * a cancellation records the pending request and stops billing at period end —
 * it destroys nothing. `effectiveAt` is the subscription's current-period-end,
 * i.e. the date the agent keeps running until (the date `CustomerAgent.cancelRequested`
 * renders); `null` when the subscription carries no period-end. The period-end
 * sweep (separate ticket) performs the teardown.
 * @since 1.0.0
 */
export const InstallationCancelResponse = Schema.Struct({
  installationId: Schema.String,
  cancellationRequested: Schema.Boolean,
  effectiveAt: Schema.NullOr(Schema.DateTimeUtc),
});
export type InstallationCancelResponse = Schema.Schema.Type<typeof InstallationCancelResponse>;

/**
 * Per-agent input for the single-agent read endpoints (`get`/`metrics`). The
 * `agentId` names the target installation; ownership is enforced server-side
 * (a non-owned agent is {@link Forbidden}, never a 404 that leaks existence).
 *
 * @since 1.0.0
 */
export const InstallationByAgentInput = Schema.Struct({
  agentId: Schema.NonEmptyTrimmedString,
});
export type InstallationByAgentInput = Schema.Schema.Type<typeof InstallationByAgentInput>;

/**
 * Input for the customer-scoped activity feed (`installations.activity`). The
 * `agentId` names the owned installation (ownership enforced server-side — a
 * non-owned agent is {@link Forbidden}, never a 404 that leaks existence).
 * `limit` defaults server-side; `before` is a UTC timestamp cursor for keyset
 * pagination (the "Load more" affordance passes the last page's oldest stamp).
 * Mirrors the admin `InstallsActivityInput` minus its operator-supplied
 * `installId` (the customer never names the row — the auth context scopes it).
 *
 * @since 1.0.0
 */
export const InstallationActivityInput = Schema.Struct({
  agentId: Schema.NonEmptyTrimmedString,
  limit: Schema.optional(Schema.Int),
  before: Schema.optional(Schema.DateTimeUtc),
});
export type InstallationActivityInput = Schema.Schema.Type<typeof InstallationActivityInput>;

/**
 * Response for `installations.activity`: the owned agent's provisioning /
 * invoice / subscription events merged into one reverse-chrono list. Reuses the
 * admin {@link InstallActivityEvent} projection verbatim — the wire shape is
 * identical, only the ownership scoping differs.
 *
 * @since 1.0.0
 */
export const InstallationActivityResponse = Schema.Array(InstallActivityEvent);
export type InstallationActivityResponse = Schema.Schema.Type<typeof InstallationActivityResponse>;

/**
 * Optional org scope for the caller's dashboard reads (`installations.list` /
 * `installations.summary`). When `customerId` is present it only SELECTS among
 * the caller's memberships (a non-member id is {@link Forbidden}, existence
 * never leaked); when absent the default org is resolved server-side
 * (subscription-bearing, else first membership). Omitting the field entirely is
 * back-compatible with the pre-multi-org single-org behavior.
 *
 * @since 1.0.0
 */
export const InstallationScopeInput = Schema.Struct({
  customerId: Schema.optional(Schema.NonEmptyTrimmedString),
});
export type InstallationScopeInput = Schema.Schema.Type<typeof InstallationScopeInput>;

/**
 * One agent installation the authenticated caller owns. Resolved on the
 * backend from `CurrentActor.person.id` via the
 * `customer_members → agent_installations` chain — never from client input.
 *
 * `health` is the Valkey-heartbeat verdict the admin fleet uses; `lastSeenAt`
 * the heartbeat moment (null when never seen). `gatewayHost`/`webHost` are the
 * derived single-label customer hosts (null when the customer has no slug) —
 * NEVER any secret. `subscriptionStatus` is the status of the agent's own
 * installation-scoped subscription, or null when none.
 *
 * `cloudProvider` is the VPS vendor the agent runs on (always set — the column
 * defaults to `digitalocean`). `seatBilling` is how the seat is billed and is
 * nullable (null = legacy row, treated as `charged` by the screen); the label
 * decision is left to the client rather than defaulted server-side.
 *
 * @since 1.0.0
 */
export const InstallationSummary = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  deploymentType: Schema.NullOr(DeploymentType),
  status: Schema.String,
  health: NodeHealth,
  lastSeenAt: Schema.NullOr(Schema.DateTimeUtc),
  region: Schema.String,
  agentVersion: Schema.NullOr(Schema.String),
  gatewayHost: Schema.NullOr(Schema.String),
  webHost: Schema.NullOr(Schema.String),
  subscriptionStatus: Schema.NullOr(Schema.String),
  cloudProvider: CloudProvider,
  seatBilling: Schema.NullOr(SeatBillingMode),
  /**
   * When this agent was ADOPTED — a box the customer already ran, brought under
   * management rather than provisioned by us. `null` for every provisioned
   * agent, which is what makes it the tunnelled/not-tunnelled discriminator on
   * the detail's {@link InstallationAccess}.
   */
  adoptedAt: Schema.NullOr(Schema.DateTimeUtc),
});
export type InstallationSummary = Schema.Schema.Type<typeof InstallationSummary>;

/**
 * Response for `installations.list`: the caller's agents (excluding
 * deleted/deprovisioned), ordered by name.
 *
 * @since 1.0.0
 */
export const InstallationListResponse = Schema.Array(InstallationSummary);
export type InstallationListResponse = Schema.Schema.Type<typeof InstallationListResponse>;

/**
 * The customer-facing access block on an agent detail: deployment type plus the
 * two derived customer hosts. `mgmt` is deliberately absent — operator-only.
 *
 * @since 1.0.0
 */
export const InstallationAccess = Schema.Struct({
  deploymentType: Schema.NullOr(DeploymentType),
  gatewayHost: Schema.NullOr(Schema.String),
  webHost: Schema.NullOr(Schema.String),
  /**
   * Whether customer traffic reaches this agent through our frp tunnel. True
   * for a provisioned agent; false for an ADOPTED one, which the customer
   * already reaches directly.
   */
  tunnelled: Schema.Boolean,
});
export type InstallationAccess = Schema.Schema.Type<typeof InstallationAccess>;

/**
 * The most recent openclaw self-report for the agent, derived from the install
 * row's stored health blob. Surfaced on the detail so the dashboard can render a
 * richer health line than the heartbeat-derived `health` verdict. `observedAt`
 * is converted to `DateTime.Utc` at the producer (null when unparseable).
 *
 * @since 1.0.0
 */
export const OcHealthRaw = Schema.Struct({
  status: Schema.Literal("ok", "degraded", "down"),
  reasonCode: Schema.NullOr(Schema.String),
  observedAt: Schema.NullOr(Schema.DateTimeUtc),
});
export type OcHealthRaw = Schema.Schema.Type<typeof OcHealthRaw>;

/**
 * The agent's installation-scoped subscription, surfaced on the detail so the
 * dashboard can show billing state without a follow-up RPC. No secret fields.
 *
 * @since 1.0.0
 */
export const InstallationSubscription = Schema.Struct({
  id: Schema.String,
  status: Schema.String,
  plan: Schema.String,
  amount: Schema.NullOr(Schema.Number),
});
export type InstallationSubscription = Schema.Schema.Type<typeof InstallationSubscription>;

/**
 * Response for `installations.get`: the list fields plus the raw openclaw
 * health self-report, the creation stamp, the {@link InstallationAccess} block
 * and the installation-scoped {@link InstallationSubscription} (null when none).
 *
 * @since 1.0.0
 */
export const InstallationDetail = Schema.Struct({
  ...InstallationSummary.fields,
  ocHealthRaw: Schema.NullOr(OcHealthRaw),
  createdAt: Schema.DateTimeUtc,
  access: InstallationAccess,
  subscription: Schema.NullOr(InstallationSubscription),
  /**
   * The reseller behind the agent's owning customer, projected to a support
   * contact so the agent-detail surface can render "Provisioned by <name>" with
   * real data. `null` when the customer has no reseller (UI falls back to
   * ClawInstall support).
   */
  supportContact: Schema.NullOr(SupportContact),
});
export type InstallationDetail = Schema.Schema.Type<typeof InstallationDetail>;

/**
 * Response for `installations.gatewayToken`: the agent's decrypted OpenClaw
 * gateway access token, revealed to the owning customer so they can complete the
 * OpenClaw Control login the gateway URL lands on. This is the ONE
 * customer-facing endpoint that intentionally returns a secret — every other
 * read endpoint here is secret-free. Ownership is enforced server-side (a
 * non-owned agent is {@link Forbidden}); the value is never logged.
 *
 * @since 1.0.0
 */
export const InstallationGatewayToken = Schema.Struct({
  agentId: Schema.String,
  gatewayToken: Schema.String,
});
export type InstallationGatewayToken = Schema.Schema.Type<typeof InstallationGatewayToken>;

/**
 * Response for `installations.webdavCredentials`: everything a client needs to
 * mount the agent's per-install WebDAV share. `url` is the path-on-apex mount
 * point (`https://<baseDomain>/a/<agentName>/webdav`), `username` is the agent
 * name, and `password` is the decrypted per-install `webdav_password` — lazily
 * minted (find-or-create) on first fetch. Like {@link InstallationGatewayToken}
 * this is a deliberate secret-returning endpoint: ownership is enforced
 * server-side (a non-owned agent is {@link Forbidden}) and the value is never
 * logged.
 *
 * @since 1.0.0
 */
export const InstallationWebdavCredentials = Schema.Struct({
  agentId: Schema.String,
  url: Schema.String,
  username: Schema.String,
  password: Schema.String,
});
export type InstallationWebdavCredentials = Schema.Schema.Type<typeof InstallationWebdavCredentials>;

/**
 * Response for `installations.summary`: the caller's customer-scoped fleet
 * aggregate — agent counts by heartbeat health, summed active sessions and
 * token spend across the fleet, plus the agent-level attention feed.
 *
 * @since 1.0.0
 */
export const InstallationFleetSummary = Schema.Struct({
  totalAgents: Schema.Number,
  online: Schema.Number,
  offline: Schema.Number,
  unknown: Schema.Number,
  activeSessions: Schema.Number,
  tokenSpendTotal: Schema.Number,
  attention: Schema.Array(AttentionItem),
});
export type InstallationFleetSummary = Schema.Schema.Type<typeof InstallationFleetSummary>;

/**
 * Response for `installations.pairingRequests`: the owned agent's current
 * pending OpenClaw device-pairing set (device fingerprints awaiting the
 * customer's approval). Empty when the gateway has no parked requests. The
 * read also nudges the agent to re-report its live set, so a mid-login request
 * appears within one poll interval.
 *
 * @since 1.0.0
 */
export const InstallationPairingRequestsResponse = Schema.Array(PendingPairingRequest);
export type InstallationPairingRequestsResponse = Schema.Schema.Type<typeof InstallationPairingRequestsResponse>;

/**
 * Input for `installations.approvePairing` / `installations.rejectPairing`. The
 * `agentId` names the target installation (ownership enforced server-side); the
 * `requestId` is the pending pairing request the customer is blessing/denying.
 *
 * @since 1.0.0
 */
export const InstallationPairingActionInput = Schema.Struct({
  agentId: Schema.NonEmptyTrimmedString,
  requestId: Schema.NonEmptyTrimmedString,
});
export type InstallationPairingActionInput = Schema.Schema.Type<typeof InstallationPairingActionInput>;

/**
 * Response for `installations.approvePairing` / `installations.rejectPairing`.
 * `dispatched` acknowledges the approve/reject command was enqueued onto the
 * agent's command stream; the gateway acts on it asynchronously (the dashboard
 * confirms by the request disappearing on the next poll).
 *
 * @since 1.0.0
 */
export const InstallationPairingActionResponse = Schema.Struct({
  agentId: Schema.String,
  requestId: Schema.String,
  dispatched: Schema.Boolean,
});
export type InstallationPairingActionResponse = Schema.Schema.Type<typeof InstallationPairingActionResponse>;

/**
 * Input for `installations.grantAccess` — the owned installation to share plus
 * the recipient's email. Granting to an email with no `people` row yet creates
 * an unbound "pending person"; the grant activates when they first sign in
 * (CLA-584). Ownership of `agentId` is enforced server-side.
 *
 * @since 1.0.0
 */
export const InstallationGrantAccessInput = Schema.Struct({
  agentId: Schema.NonEmptyTrimmedString,
  email: Schema.NonEmptyTrimmedString,
});
export type InstallationGrantAccessInput = Schema.Schema.Type<typeof InstallationGrantAccessInput>;

/**
 * Input for `installations.revokeAccess` — the owned installation and the
 * person whose access to it is being revoked. Ownership of `agentId` is
 * enforced server-side.
 *
 * @since 1.0.0
 */
export const InstallationRevokeAccessInput = Schema.Struct({
  agentId: Schema.NonEmptyTrimmedString,
  personId: Schema.NonEmptyTrimmedString,
});
export type InstallationRevokeAccessInput = Schema.Schema.Type<typeof InstallationRevokeAccessInput>;

/**
 * One person who holds an access grant on the agent, projected for the
 * share-access panel. `pending` is true when the person has not signed up yet
 * (null `clerk_id`) — the invite has been created but not yet redeemed.
 * `createdAt` is the grant moment. No secret or cross-entity fields.
 *
 * @since 1.0.0
 */
export const InstallationAccessGrantView = Schema.Struct({
  personId: Schema.String,
  email: Schema.String,
  name: Schema.String,
  pending: Schema.Boolean,
  createdAt: Schema.DateTimeUtc,
});
export type InstallationAccessGrantView = Schema.Schema.Type<typeof InstallationAccessGrantView>;

/**
 * Response for `installations.listAccess`: everyone with an access grant on the
 * agent, ordered by grant time (newest first).
 *
 * @since 1.0.0
 */
export const InstallationAccessListResponse = Schema.Array(InstallationAccessGrantView);
export type InstallationAccessListResponse = Schema.Schema.Type<typeof InstallationAccessListResponse>;

/**
 * Customer-facing installation RPC group.
 *
 * `requestCancellation` verifies the caller owns the installation (via the
 * customer membership chain) and dispatches the durable VPS deprovisioning
 * workflow. The read endpoints are all customer-scoped and read-only; the
 * per-agent ones (`get`/`metrics`) enforce ownership with a {@link Forbidden}
 * that never leaks existence.
 *
 * The pairing endpoints (`pairingRequests`/`approvePairing`/`rejectPairing`)
 * back the 2FA-style device-pairing approval flow: the customer sees pending
 * gateway logins in the dashboard and blesses them, all ownership-enforced.
 *
 * @since 1.0.0
 */
export class InstallationRpcs extends RpcGroup.make(
  Rpc.make("installations.requestCancellation", {
    payload: InstallationCancelInput,
    success: InstallationCancelResponse,
    // BrandMiddleware + SubscriptionError join the union because the request now
    // stops billing at period end on the agent's OWN subscription, which talks to
    // the brand's Stripe account — the same stack `cancelSubscription` /
    // `resumeSubscription` already use (CLA-1003).
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError, SubscriptionError),
  }).middleware(AuthMiddleware).middleware(BrandMiddleware),
  /**
   * Return the agent installations the authenticated caller owns. The optional
   * {@link InstallationScopeInput} `customerId` selects among the caller's
   * memberships (a non-member id is {@link Forbidden}); omitted resolves the
   * default org (subscription-bearing, else first membership) from `CurrentActor`.
   */
  Rpc.make("installations.list", {
    payload: InstallationScopeInput,
    success: InstallationListResponse,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError, ValkeyError),
  }).middleware(AuthMiddleware),
  /**
   * Return one owned agent's detail (access hosts + subscription + raw health).
   * Ownership enforced — a non-owned agent is {@link Forbidden}.
   */
  Rpc.make("installations.get", {
    payload: InstallationByAgentInput,
    success: InstallationDetail,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError, ValkeyError),
  }).middleware(AuthMiddleware),
  /**
   * Return one owned agent's activity feed — provisioning, invoice and
   * subscription events merged into a single reverse-chrono list, paged by
   * `limit` / `before`. Ownership enforced — a non-owned agent is
   * {@link Forbidden}, never a 404 that leaks existence. The customer-scoped
   * sibling of the admin `installs.activity`: same event shape and merge, only
   * the ownership swap differs. Read-only; no live streaming.
   */
  Rpc.make("installations.activity", {
    payload: InstallationActivityInput,
    success: InstallationActivityResponse,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AuthMiddleware),
  /**
   * Return one owned agent's usage metrics (cpu/memory/sessions/token/request).
   * Ownership enforced — a non-owned agent is {@link Forbidden}.
   */
  Rpc.make("installations.metrics", {
    payload: InstallationByAgentInput,
    success: NodeMetrics,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError, ValkeyError),
  }).middleware(AuthMiddleware),
  /**
   * Reveal one owned agent's decrypted gateway access token. Ownership enforced
   * — a non-owned agent is {@link Forbidden}, never a 404 that leaks existence.
   * The dashboard surfaces this behind a click-to-reveal affordance; the value
   * is never logged.
   */
  Rpc.make("installations.gatewayToken", {
    payload: InstallationByAgentInput,
    success: InstallationGatewayToken,
    // ValidationError: an adopted box has no gateway token we ever minted.
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError, ValidationError),
  }).middleware(AuthMiddleware),
  /**
   * Lazily mint (find-or-create, race-safe) and reveal one owned agent's WebDAV
   * mount credential (URL + username + decrypted `webdav_password`). Ownership
   * enforced — a non-owned agent is {@link Forbidden}, never a 404 that leaks
   * existence. Scope is deliberately customer-membership only (an access-grant
   * holder cannot pull the secret); the value is never logged. The dashboard
   * (890) and the mount helper (889) both consume this.
   */
  Rpc.make("installations.webdavCredentials", {
    payload: InstallationByAgentInput,
    success: InstallationWebdavCredentials,
    // ValidationError: an adopted box serves no WebDAV share to mount.
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError, ValidationError),
  }).middleware(AuthMiddleware),
  /**
   * Return the caller's customer-scoped fleet aggregate. The optional
   * {@link InstallationScopeInput} `customerId` selects among the caller's
   * memberships (a non-member id is {@link Forbidden}); omitted resolves the
   * default org (subscription-bearing, else first membership).
   */
  Rpc.make("installations.summary", {
    payload: InstallationScopeInput,
    success: InstallationFleetSummary,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError, ValkeyError),
  }).middleware(AuthMiddleware),
  /**
   * Return one owned agent's pending device-pairing requests. Ownership
   * enforced — a non-owned agent is {@link Forbidden}. The read also dispatches
   * a `devices.list` refresh to the agent so the set stays current while the
   * customer polls mid-login.
   */
  Rpc.make("installations.pairingRequests", {
    payload: InstallationByAgentInput,
    success: InstallationPairingRequestsResponse,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError, ValkeyError),
  }).middleware(AuthMiddleware),
  /**
   * Approve one pending device-pairing request on an owned agent. Ownership
   * enforced — a non-owned agent is {@link Forbidden}. Dispatches a
   * `devices.approve` command through the agent's command stream.
   */
  Rpc.make("installations.approvePairing", {
    payload: InstallationPairingActionInput,
    success: InstallationPairingActionResponse,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError, ValkeyError),
  }).middleware(AuthMiddleware),
  /**
   * Reject one pending device-pairing request on an owned agent. Ownership
   * enforced — a non-owned agent is {@link Forbidden}. Dispatches a
   * `devices.reject` command through the agent's command stream.
   */
  Rpc.make("installations.rejectPairing", {
    payload: InstallationPairingActionInput,
    success: InstallationPairingActionResponse,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError, ValkeyError),
  }).middleware(AuthMiddleware),
  /**
   * List everyone who holds an access grant on one owned agent. Ownership
   * enforced — a non-owned agent is {@link Forbidden}, never a 404 that leaks
   * existence. Backs the agent-detail share panel (CLA-584).
   */
  Rpc.make("installations.listAccess", {
    payload: InstallationByAgentInput,
    success: InstallationAccessListResponse,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AuthMiddleware),
  /**
   * Grant a person (by email) access to one owned agent — the self-serve
   * counterpart of the admin `grantAgentAccess` (CLA-583), scoped to ownership.
   * An email with no `people` row yet creates an unbound pending person and the
   * grant activates on their first sign-in. Ownership enforced — a non-owned
   * agent is {@link Forbidden}. `granted_by` is stamped from `CurrentActor`,
   * never a client field.
   */
  Rpc.make("installations.grantAccess", {
    payload: InstallationGrantAccessInput,
    success: InstallationAccessGrantView,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AuthMiddleware),
  /**
   * Revoke a person's access to one owned agent and bust the k8s-proxy
   * allow-cache for the affected `(person, node)` so access stops before the
   * cache TTL. Ownership enforced — a non-owned agent is {@link Forbidden};
   * a person with no grant on the agent is {@link RecordNotFoundError}.
   */
  Rpc.make("installations.revokeAccess", {
    payload: InstallationRevokeAccessInput,
    success: Schema.Void,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError, ValkeyError),
  }).middleware(AuthMiddleware),
) {}
